import { type ChangeEvent, type FormEvent, useState } from 'react';
import { api } from '../../../api/client';
import styles from './StaffForm.module.css';

interface StaffData {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  position?: string;
  role?: string;
  isActive?: boolean;
}

interface StaffFormProps {
  onClose: () => void;
  onSuccess: () => void;
  staff?: StaffData;
}

export default function StaffForm({ onClose, onSuccess, staff }: StaffFormProps) {
  const [formData, setFormData] = useState({
    name: staff?.name || '',
    email: staff?.email || '',
    password: '',
    phone: staff?.phone || '',
    position: staff?.position || '',
    role: staff?.role || 'STAFF',
    isActive: staff?.isActive ?? true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const isEditing = !!staff?.id;

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditing) {
        const body: Record<string, unknown> = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          position: formData.position || undefined,
          role: formData.role,
          isActive: formData.isActive,
        };
        if (formData.password) body.password = formData.password;

        await api.put(`/api/misc/staff/${staff.id}`, body);
        onSuccess();
        setTimeout(() => onClose(), 1500);
      } else {
        const res = await api.post<any>('/api/misc/staff', {
          name: formData.name,
          email: formData.email,
          password: formData.password || undefined,
          phone: formData.phone || undefined,
          position: formData.position || undefined,
          role: formData.role,
        });

        // Show temp password
        setTempPassword(res.temporaryPassword || formData.password || '');
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const title = isEditing ? 'Editar Empleado' : 'Nuevo Empleado';
  const btnText = isEditing
    ? loading
      ? 'Guardando...'
      : 'Guardar Cambios'
    : loading
      ? 'Guardando...'
      : 'Registrar Empleado';

  // Show credentials after creation
  if (tempPassword !== null) {
    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2>✅ Empleado Registrado</h2>
            <button onClick={onClose} className={styles.closeBtn}>
              &times;
            </button>
          </div>

          <div style={{ padding: '1rem 0' }}>
            <p style={{ marginBottom: '1rem', color: '#555' }}>
              Entregale estas credenciales al usuario. Al ingresar por primera vez, el sistema le
              pedirá cambiar la contraseña.
            </p>

            <div
              style={{
                background: '#f5f7ff',
                border: '1px solid #d0dfff',
                borderRadius: 12,
                padding: '1.2rem',
                marginBottom: '1rem',
              }}
            >
              <div style={{ marginBottom: '0.8rem' }}>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#999',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  Usuario
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1a1c2c' }}>
                  {formData.email}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#999',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  Contraseña temporal
                </div>
                <div
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    color: '#1a1c2c',
                    fontFamily: 'monospace',
                    background: '#fff',
                    padding: '0.4rem 0.8rem',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    display: 'inline-block',
                    letterSpacing: '0.1em',
                  }}
                >
                  {tempPassword}
                </div>
              </div>
            </div>

            <div
              style={{
                background: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 12,
                padding: '0.8rem 1rem',
                fontSize: '0.85rem',
                color: '#e65100',
              }}
            >
              ⚠️ Esta es la única vez que se muestra la contraseña. Copiala antes de cerrar.
            </div>
          </div>

          <div className={styles.footer}>
            <button onClick={onClose} className={styles.submitBtn} style={{ width: '100%' }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>{title}</h2>
          <button onClick={onClose} className={styles.closeBtn}>
            &times;
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label>Nombre completo</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Ej: Juan Pérez"
              />
            </div>
            <div className={styles.field}>
              <label>Correo electrónico</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div className={styles.field}>
              <label>
                Contraseña
                {isEditing
                  ? ' (dejar vacío para no cambiar)'
                  : ' (opcional — si no ponés, se genera una automáticamente)'}
              </label>
              <input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                minLength={isEditing ? 0 : 0}
                placeholder={isEditing ? '••••••••' : 'Dejá vacío para generar automática'}
              />
            </div>
            <div className={styles.field}>
              <label>Rol</label>
              <select name="role" value={formData.role} onChange={handleChange}>
                <option value="STAFF">Personal</option>
                <option value="TEACHER">Docente</option>
                <option value="SCHOOL_ADMIN">Administrador</option>
              </select>
            </div>
            <div className={styles.field}>
              <label>Teléfono</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="555-1234"
              />
            </div>
            <div className={styles.field}>
              <label>Cargo</label>
              <input
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="Ej: Secretaria, Prefecto..."
              />
            </div>
            {isEditing && (
              <div className={styles.fieldCheck}>
                <label>
                  <input
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  Usuario activo
                </label>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <button type="button" onClick={onClose} className={styles.cancelBtn}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} className={styles.submitBtn}>
              {btnText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
